import React, { useEffect, useRef, type MutableRefObject } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { useSnippet } from './useSnippet';
import SnippetEditor from './SnippetEditor';
import SnippetPreview from './SnippetPreview';
import SnippetToolbar from './SnippetToolbar';
import '../../styles/features/snippet/SnippetPage.css';

interface Props {
  onClose: () => void;
}

const SnippetPage: React.FC<Props> = ({ onClose }) => {
  const previewRef = useRef<HTMLDivElement>(null) as MutableRefObject<HTMLDivElement | null>;
  const {
    code,
    language,
    theme,
    title,
    copyCodeStatus,
    copyLinkStatus,
    downloadStatus,
    setCode,
    setLanguage,
    setTheme,
    setTitle,
    handleCopyCode,
    handleSaveAndCopyLink,
    handleDownload,
  } = useSnippet();

  // ESC key closes the overlay
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll while overlay is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const onDownloadClick = () => {
    handleDownload(previewRef);
  };

  return (
    <motion.div
      className="snippet-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      role="dialog"
      aria-modal="true"
      aria-label="Snippet Share"
    >
      {/* Header */}
      <header className="snippet-page__header">
        <button
          type="button"
          className="snippet-page__back"
          onClick={onClose}
          aria-label="Back to portfolio"
        >
          <ChevronLeft size={18} />
          <span>Back to Portfolio</span>
        </button>

        <div className="snippet-page__title-group">
          <h1 className="snippet-page__title">
            Snippet Share
          </h1>
          <p className="snippet-page__subtitle">
            Paste, style, and share beautiful code snippets
          </p>
        </div>
      </header>

      {/* Body */}
      <div className="snippet-page__body">
        {/* Toolbar */}
        <SnippetToolbar
          title={title}
          language={language}
          theme={theme}
          copyCodeStatus={copyCodeStatus}
          copyLinkStatus={copyLinkStatus}
          downloadStatus={downloadStatus}
          onTitleChange={setTitle}
          onLanguageChange={setLanguage}
          onThemeChange={setTheme}
          onCopyCode={handleCopyCode}
          onCopyLink={handleSaveAndCopyLink}
          onDownload={onDownloadClick}
        />

        {/* Editor + Preview panels */}
        <div className="snippet-page__panels">
          <SnippetEditor
            code={code}
            onChange={setCode}
            language={language}
          />
          <SnippetPreview
            code={code}
            language={language}
            theme={theme}
            title={title}
            previewRef={previewRef}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default SnippetPage;
